import { usePersona } from "@next-platform/client-common-core";
import { useCallback } from "react";

import { sessionStorageService } from "@/utils/sessionStorageRe";

const CHAT_DRAFT_KEY_PREFIX = "chatDraft";

type UseChatDraftArgs = {
  characterId?: string;
  conversationId?: string | null;
};

// A draft is scoped per conversation. Before a conversation id exists (a brand
// new chat), it is scoped to the character and persona so a new chat starts empty without
// wiping other conversations' drafts or carrying over across personas.
const buildDraftKey = ({ characterId, conversationId, personaId }: UseChatDraftArgs & { personaId?: string | null }) => {
  const scope = conversationId ? conversationId : `new:${characterId ?? ""}:${personaId ?? ""}`;
  return `${CHAT_DRAFT_KEY_PREFIX}:${scope}`;
};

const useChatDraft = ({ characterId, conversationId }: UseChatDraftArgs) => {
  const { selectedPersona, getDefaultPersona } = usePersona();
  const defaultPersona = getDefaultPersona();
  const personaId = selectedPersona?.id || defaultPersona?.id || null;

  const draftKey = buildDraftKey({ characterId, conversationId, personaId });

  const loadDraft = useCallback(() => sessionStorageService.getItem(draftKey) ?? "", [draftKey]);

  const saveDraft = useCallback(
    (text: string) => {
      if (text.trim().length > 0) {
        sessionStorageService.setItem(draftKey, text);
      } else {
        // Never persist an empty draft; nothing to restore later.
        sessionStorageService.removeItem(draftKey);
      }
    },
    [draftKey],
  );

  const clearDraft = useCallback(() => {
    sessionStorageService.removeItem(draftKey);
  }, [draftKey]);

  return { loadDraft, saveDraft, clearDraft };
};

export default useChatDraft;
